import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';

import {
  SelectedItemRow,
  type SelectedItemRowSecondaryAction,
} from './SelectedItemRow';

export interface SelectedItemRowsProps<T> {
  items: T[];
  getItemValue: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemImage?: (item: T) => string | undefined;
  renderContent?: (item: T) => ReactNode;
  secondaryActions?: (item: T) => SelectedItemRowSecondaryAction[];
  onRemove: (itemValue: string) => void;
  onReorder: (items: T[]) => void;
  removeLabel?: string;
  disabled?: boolean;
}

export function SelectedItemRows<T>({
  items,
  getItemValue,
  getItemLabel,
  getItemImage,
  renderContent,
  secondaryActions,
  onRemove,
  onReorder,
  removeLabel,
  disabled = false,
}: SelectedItemRowsProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (disabled) {
      return;
    }

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(
        (item) => getItemValue(item) === active.id,
      );
      const newIndex = items.findIndex(
        (item) => getItemValue(item) === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      onReorder(arrayMove(items, oldIndex, newIndex));
    }
  };

  if (!items.length) {
    return null;
  }

  const itemIds = items.map((item) => getItemValue(item));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-xs">
          {items.map((item) => {
            const itemValue = getItemValue(item);

            return (
              <SelectedItemRow
                key={itemValue}
                id={itemValue}
                label={getItemLabel(item)}
                imageSrc={getItemImage?.(item)}
                renderContent={
                  renderContent ? () => renderContent(item) : undefined
                }
                secondaryActions={secondaryActions?.(item)}
                onRemove={() => onRemove(itemValue)}
                removeLabel={removeLabel}
                disabled={disabled}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

SelectedItemRows.displayName = 'SelectedItemRows';
